var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useCallback, useMemo, useState } from 'react';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { useClasses, useConsoleLog, useControlledStateWarning, useLayoutProps, useProps, useTranslate, } from '@hawkins/helpers';
import { TimePickerStrings } from './timePicker.strings';
import { useTimePickerStyles } from './timePicker.styles';
import { TimePickerTestIds } from './timePicker.testids';
import { formatTimeNumber, getDisplayHourForDate, getDisplayMinuteForDate, getDisplayPeriodForDate, getDisplaySecondForDate, getTimePickerOptions, getUpdatedDateForHour, getUpdatedDateForMinute, getUpdatedDateForPeriod, getUpdatedDateForSecond, } from './timePicker.utils';
import { TimePickerOptionsSection } from './timePickerOptionsSection';
var displayName = 'TimePicker';
/**
 * `<TimePicker>` allows the user to select a point in time.
 *
 * `<TimePicker>` consists of the picker itself. `<TimePickerDropdown>` and `<TimePickerInput>`
 * can be used to render the picker in the context of a dropdown toggle or input field.
 *
 * If the control you are looking for is not represented by any of these components,
 * you can use `<Popover>` to create a custom time picker according to your needs.
 */
export var TimePicker = forwardRef(function (props, ref) {
    // Props
    var _a = useProps(displayName, props), classesProp = _a.classes, className = _a.className, _b = _a.component, Component = _b === void 0 ? 'div' : _b, dateProp = _a.date, _c = _a.defaultDate, defaultDate = _c === void 0 ? null : _c, _d = _a.disabled, disabled = _d === void 0 ? false : _d, _e = _a.isUTC, isUTC = _e === void 0 ? false : _e, layout = _a.layout, onChange = _a.onChange, styleProp = _a.style, translateProp = _a.translate, _f = _a.use12HourClock, use12HourClock = _f === void 0 ? false : _f, _g = _a.withSeconds, withSeconds = _g === void 0 ? false : _g, restProps = __rest(_a, ["classes", "className", "component", "date", "defaultDate", "disabled", "isUTC", "layout", "onChange", "style", "translate", "use12HourClock", "withSeconds"]);
    useControlledStateWarning(dateProp, { displayName: displayName });
    var _h = useMemo(function () { return getTimePickerOptions({ use12HourClock: use12HourClock, withSeconds: withSeconds }); }, [use12HourClock, withSeconds]), hours = _h.hours, minutes = _h.minutes, periods = _h.periods, seconds = _h.seconds;
    // Strings
    var translate = useTranslate(translateProp, TimePickerStrings);
    // State
    var _j = __read(useState(defaultDate || null), 2), internalDate = _j[0], setInternalDate = _j[1];
    var isDateControlled = dateProp !== undefined;
    var date = isDateControlled ? dateProp : internalDate;
    useConsoleLog(!!date && !dayjs(date).isValid(), 'Invalid date given', { displayName: displayName });
    var onHourChange = useCallback(function (hour) {
        var newDate = getUpdatedDateForHour(hour, date, { isUTC: isUTC, use12HourClock: use12HourClock });
        if (!isDateControlled)
            setInternalDate(newDate);
        onChange === null || onChange === void 0 ? void 0 : onChange(newDate);
    }, [date, isDateControlled, isUTC, onChange, use12HourClock]);
    var onMinuteChange = useCallback(function (minute) {
        var newDate = getUpdatedDateForMinute(minute, date, { isUTC: isUTC });
        if (!isDateControlled)
            setInternalDate(newDate);
        onChange === null || onChange === void 0 ? void 0 : onChange(newDate);
    }, [date, isDateControlled, isUTC, onChange]);
    var onSecondChange = useCallback(function (second) {
        var newDate = getUpdatedDateForSecond(second, date, { isUTC: isUTC });
        if (!isDateControlled)
            setInternalDate(newDate);
        onChange === null || onChange === void 0 ? void 0 : onChange(newDate);
    }, [date, isDateControlled, isUTC, onChange]);
    var onPeriodChange = useCallback(function (period) {
        var newDate = getUpdatedDateForPeriod(period, date, { isUTC: isUTC });
        if (!isDateControlled)
            setInternalDate(newDate);
        onChange === null || onChange === void 0 ? void 0 : onChange(newDate);
    }, [date, isDateControlled, isUTC, onChange]);
    // Styles
    var baseClasses = useTimePickerStyles();
    var classes = useClasses(displayName, baseClasses, classesProp);
    var style = useLayoutProps({ layout: layout, style: styleProp });
    var rootClassName = clsx(classes.HAWKINS_BASE, className, classes.root);
    return (_jsxs(Component, __assign({ "aria-label": translate('timePicker.ariaLabel'), className: rootClassName, "data-testid": TimePickerTestIds.ROOT, ref: ref, role: "group", style: style }, restProps, { children: [_jsx(TimePickerOptionsSection, { classes: classes, disabled: disabled, formatOptionLabel: formatTimeNumber, onChange: onHourChange, options: hours, optionType: "hours", selectedOption: date && getDisplayHourForDate(date, { isUTC: isUTC, use12HourClock: use12HourClock }), translate: translate }), _jsx(TimePickerOptionsSection, { classes: classes, disabled: disabled, formatOptionLabel: formatTimeNumber, onChange: onMinuteChange, options: minutes, optionType: "minutes", selectedOption: date && getDisplayMinuteForDate(date, { isUTC: isUTC }), translate: translate }), !!seconds.length && (_jsx(TimePickerOptionsSection, { classes: classes, disabled: disabled, formatOptionLabel: formatTimeNumber, onChange: onSecondChange, options: seconds, optionType: "seconds", selectedOption: date && getDisplaySecondForDate(date, { isUTC: isUTC }), translate: translate })), !!periods.length && (_jsx(TimePickerOptionsSection, { classes: classes, disabled: disabled, onChange: onPeriodChange, options: periods, optionType: "periods", selectedOption: date && getDisplayPeriodForDate(date, { isUTC: isUTC }), translate: translate }))] })));
});
TimePicker.displayName = displayName;
